/**
 * Concrete `PushSubscriptionRegistrar` para o app cliente web.
 *
 * Bridge entre a interface de `@meiuca/push` e o HTTP autenticado do app
 * (`BaseMeiucaService` de `@meiuca/service`). Mirror dos
 * `DefaultPushSubscriptionRegistrar` no iOS / Android:
 *   - Hardcoda `deviceType = 'web'` — esse app só roda em browser.
 *   - O backend (`MobileAppApi.RegisterPushSubscriptionController`) deriva
 *     `userType = 'app_user'` do bearer; o body não precisa carregar.
 */

import type { PushSubscriptionRegistrar, PushToken } from '@meiuca/push'
import { BaseMeiucaService } from './MeiucaService'
import { env } from '@/lib/env'

interface RegisterPushSubscriptionRequest {
  token: string
  provider: string
  deviceType: string
  deviceIdentifier: string
  providerData?: Record<string, string>
  userAgent?: string
}

interface PushSubscriptionResponse {
  id: string
  token: string
  provider: string
  deviceType: string
  isActive: boolean
  createdAt: string
}

class PushSubscriptionRegistrarImpl extends BaseMeiucaService implements PushSubscriptionRegistrar {
  async register(token: PushToken, deviceIdentifier: string): Promise<void> {
    // O endpoint exige sessão. Chamar deslogado devolve 401, e o handler
    // global derruba o visitante para o login — o que expulsaria quem está
    // navegando nas páginas públicas. O token é capturado no boot de todo
    // jeito; `enableBackendRegistration()` reenvia depois do login.
    if (!this.getAccessToken()) {
      return
    }

    const request: RegisterPushSubscriptionRequest = {
      token: token.value,
      provider: token.providerKind,
      deviceType: 'web',
      deviceIdentifier,
      userAgent: navigator.userAgent,
    }
    await this.post<PushSubscriptionResponse>('/notifications/push/subscribe', request)
  }
}

export const pushSubscriptionRegistrar: PushSubscriptionRegistrar = new PushSubscriptionRegistrarImpl({
  baseURL: env.API_URL,
  refreshEndpoint: '/auth/refresh-token',
  cookiePrefix: '',
  debug: env.IS_DEVELOPMENT,
})
